import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { readEdgeError } from "@/lib/edgeError";
import { isManualTemplateUsable, templatePreview, type ManualTemplate } from "@/lib/replyWindow";

// Once the 24h window has closed, Meta only delivers an approved template.
// Lets the owner restart the chat with one of the business's approved
// WhatsApp templates. The server fills in the customer's name and re-checks
// approval and opt-outs (send-channel-message, template_id).
export function ReplyTemplatePicker({
  businessId,
  conversationId,
}: {
  businessId: string;
  conversationId: string;
}) {
  const [selectedId, setSelectedId] = useState<string>("");
  const [sending, setSending] = useState(false);

  const { data: templates = [] } = useQuery({
    queryKey: ["manual-reply-templates", businessId],
    enabled: !!businessId,
    queryFn: async (): Promise<ManualTemplate[]> => {
      const { data, error } = await supabase
        .from("business_message_templates")
        .select("id, body_text, body_var_order, header_format, header_text")
        .eq("business_id", businessId)
        .eq("channel", "whatsapp")
        .eq("status", "approved")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return ((data ?? []) as ManualTemplate[]).filter((t) => t.body_text && isManualTemplateUsable(t));
    },
  });

  if (templates.length === 0) return null;
  const selected = templates.find((t) => t.id === selectedId) ?? null;

  const send = async () => {
    if (!selected || sending) return;
    setSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-channel-message", {
        body: {
          business_id: businessId,
          conversation_id: conversationId,
          template_id: selected.id,
          sender_type: "human_owner",
        },
      });
      if (error) throw error;
      toast.success("Template sent");
      setSelectedId("");
    } catch (e) {
      toast.error("Not sent", { description: await readEdgeError(e, "Failed to send the template") });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-2.5 space-y-2 border-t border-border pt-2.5">
      <p className="text-foreground">Or restart the chat with an approved template:</p>
      <Select value={selectedId} onValueChange={setSelectedId} disabled={sending}>
        <SelectTrigger className="h-9 bg-background text-xs" aria-label="Choose a template">
          <SelectValue placeholder="Choose a template" />
        </SelectTrigger>
        <SelectContent>
          {templates.map((t) => (
            <SelectItem key={t.id} value={t.id} className="text-xs">
              <span className="line-clamp-1 max-w-[260px] sm:max-w-[420px]">{templatePreview(t)}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selected && (
        <>
          <p className="whitespace-pre-wrap break-words rounded-md border border-border bg-background px-3 py-2 text-foreground">
            {templatePreview(selected)}
          </p>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px]">WhatsApp charges a small fee per template.</span>
            <Button size="sm" onClick={send} disabled={sending} className="shrink-0">
              {sending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Send template
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
